import React, { Component } from 'react';

import Loading from '@qality/common-components/src/atoms/Loading/Loading';
import { IS_CLOUD } from '@qality/common-config';
import { messages } from '@qality/common-messages';
import { JiraPermissions, TestExecution, User } from '@qality/common-model';
import {
  CREATE_ATTACHMENTS,
  DELETE_ALL_ATTACHMENTS,
  DELETE_OWN_ATTACHMENTS,
  ExecutionStatusService,
  PERMISSIONS_EDIT_ISSUES,
  PermissionService,
  StorageOfUniqueServices,
  TestExecutionService,
  UserService,
} from '@qality/common-service';
import { ExecutionStatuses } from '@qality/common-service/src/ExecutionStatusService';

type Props = {
  children: JSX.Element;
};

type State = {
  testExecution: TestExecution;
  currentUser: User;
  statuses: ExecutionStatuses;
  canEdit: boolean;
  canDeleteAllAttachment: boolean;
  canCreateAttachment: boolean;
  canDeleteOwnAttachment: boolean;
  handleUpdateTestExecution: (testExecution: TestExecution) => void;
};

const defaultState: State = {
  testExecution: null,
  currentUser: null,
  statuses: null,
  canEdit: false,
  canDeleteAllAttachment: false,
  canCreateAttachment: false,
  canDeleteOwnAttachment: false,
  handleUpdateTestExecution: () => undefined,
};

export const TestExecutionPageContext =
  React.createContext<State>(defaultState);

export class TestExecutionPageContextProvider extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = defaultState;
  }

  async componentDidMount(): Promise<void> {
    await StorageOfUniqueServices.get('AttachmentConfigService').init();
    const urlSearchParams = await StorageOfUniqueServices.get(
      'URLService'
    ).getJiraURLSearchParams();
    const testExeuctionId = urlSearchParams.get('testExecutionId');
    const testExecution = await TestExecutionService.fetchTestExecution(
      testExeuctionId
    );

    const promises = [
      UserService.getCurrentUser(),
      this.getPermissions(testExecution.testCaseId),
      ExecutionStatusService.getStatuses(),
    ] as const;

    await Promise.all(promises)
      .then((values: [User, JiraPermissions, ExecutionStatuses]) => {
        const [currentUser, permissions, statuses] = values;
        this.setState({
          testExecution,
          currentUser,
          statuses,
          canEdit:
            currentUser.getIdentifier() === 'anonymous_user'
              ? false
              : permissions[PERMISSIONS_EDIT_ISSUES],
          canDeleteAllAttachment: permissions[DELETE_ALL_ATTACHMENTS],
          canCreateAttachment: permissions[CREATE_ATTACHMENTS],
          canDeleteOwnAttachment: permissions[DELETE_OWN_ATTACHMENTS],
        });
        return;
      })
      .catch((e) => {
        StorageOfUniqueServices.get('ErrorHandlingUtils').handleCriticalError(
          e,
          messages['error.testExecutionPageContext.init.message'],
          plugin.servicedeskUrl
        );
      });
  }

  async getPermissions(issueId: string | number): Promise<JiraPermissions> {
    return await PermissionService.hasPermissionsByIssueId(issueId);
  }

  handleUpdateTestExecution = (testExecution: TestExecution): void => {
    this.setState({ testExecution });
  };

  render(): JSX.Element {
    const { children } = this.props;

    return (
      <TestExecutionPageContext.Provider
        value={{
          testExecution: this.state.testExecution,
          currentUser: this.state.currentUser,
          statuses: this.state.statuses,
          canEdit: this.state.canEdit,
          canDeleteAllAttachment: this.state.canDeleteAllAttachment,
          canCreateAttachment: this.state.canCreateAttachment,
          canDeleteOwnAttachment: this.state.canDeleteOwnAttachment,
          handleUpdateTestExecution: this.handleUpdateTestExecution,
        }}
      >
        {this.state.testExecution ? children : <Loading relative={!IS_CLOUD} />}
      </TestExecutionPageContext.Provider>
    );
  }
}

export const TestExecutionPageConsumer = TestExecutionPageContext.Consumer;
